import React, { useEffect, useRef, useState } from "react";
import { Alert, Button, Form, Modal } from "react-bootstrap";
import { studentReviewApi as api, reviewFailureCode, reviewTextError, studentReviewError } from "./studentReviewApi";
import { fullReviewTextStyle } from "./StudentReviewPresentation";

export default function EditMyReviewModal({ review, onHide, onSaved, onMissing }) {
  const [target, setTarget] = useState(review);
  const [draft, setDraft] = useState(review.reviewText);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [recovery, setRecovery] = useState("");
  const [latest, setLatest] = useState(undefined);
  const pending = useRef(false);
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const canonical = async (mode) => {
    const data = await api.get(target.courseId);
    if (!mounted.current) return;
    if (!data.review) { setLatest(null); setRecovery(mode); onMissing(target.reviewId); setError("ไม่พบรีวิวนี้แล้ว ไม่สามารถบันทึกได้ ข้อความร่างยังอยู่ให้คัดลอก"); }
    else if (mode === "saved") onSaved(data.review);
    else setLatest(data.review);
  };
  const retryRead = async () => {
    if (pending.current) return;
    pending.current = true; setBusy(true);
    try { await canonical(recovery); }
    catch { if (mounted.current) setError("โหลดข้อมูลล่าสุดไม่สำเร็จ ข้อความร่างยังอยู่ กรุณาลองโหลดอีกครั้ง"); }
    finally { pending.current = false; if (mounted.current) setBusy(false); }
  };
  const save = async (event) => {
    event.preventDefault();
    if (pending.current || recovery) return;
    const invalid = reviewTextError(draft); if (invalid) { setError(invalid); return; }
    pending.current = true; setBusy(true); setError("");
    let committed = false;
    try {
      await api.update(target.reviewId, draft, target.revision);
      committed = true;
      await canonical("saved");
    } catch (failure) {
      if (!mounted.current) return;
      if (committed) { setRecovery("saved"); setError("บันทึกแล้ว แต่โหลดข้อมูลล่าสุดไม่สำเร็จ ข้อความร่างยังอยู่ กรุณาโหลดอีกครั้ง"); }
      else if (["aborted", "not-found"].includes(reviewFailureCode(failure))) {
        setRecovery("conflict"); setError(studentReviewError(failure));
        try { await canonical("conflict"); }
        catch { if (mounted.current) setError("โหลดข้อมูลล่าสุดไม่สำเร็จ ข้อความร่างยังอยู่ กรุณาลองโหลดอีกครั้ง"); }
      } else setError(studentReviewError(failure));
    } finally { pending.current = false; if (mounted.current) setBusy(false); }
  };
  return <Modal show centered scrollable onHide={() => { if (!pending.current) onHide(); }} backdrop={busy ? "static" : true} keyboard={!busy}>
      <Modal.Header closeButton={!busy}><Modal.Title>แก้ไขรีวิว</Modal.Title></Modal.Header>
      <Modal.Body>
        <Form id="edit-my-review-form" onSubmit={save}>
        <p>{review.courseName}</p>
        {target.status === "suspended" && <p>การแก้ไขไม่ยกเลิกการระงับเผยแพร่</p>}
        <Form.Group controlId="edit-review-text"><Form.Label>ข้อความรีวิว</Form.Label><Form.Control autoFocus as="textarea" rows={8} value={draft} disabled={busy} onChange={(event) => setDraft(event.target.value)} /></Form.Group>
        <p className="small mt-2">10–2,000 ตัวอักษร ไม่เกิน 8,192 ไบต์ ไม่ใส่อีเมลหรือเบอร์โทรศัพท์</p>
        {error && <Alert variant="danger">{error}</Alert>}
        {recovery && latest === undefined && <Button variant="outline-primary" disabled={busy} onClick={retryRead}>โหลดข้อมูลล่าสุด</Button>}
        {recovery === "conflict" && latest && <Alert variant="warning">
          <p>ข้อความล่าสุดในระบบ:</p><p style={fullReviewTextStyle}>{latest.reviewText}</p>
          <p>คัดลอกข้อความร่างที่ต้องการเก็บก่อน ปุ่มด้านล่างจะแทนที่ร่างด้วยข้อมูลล่าสุด และยังไม่บันทึกการแก้ไข</p>
          <Button disabled={busy} onClick={() => { setTarget(latest); setDraft(latest.reviewText); setLatest(undefined); setRecovery(""); setError(""); }}>ใช้ข้อความล่าสุดเพื่อแก้ไข</Button>
        </Alert>}
        {busy && <div role="status">กำลังบันทึกหรือโหลดข้อมูล...</div>}
        </Form>
      </Modal.Body>
      <Modal.Footer><Button variant="secondary" disabled={busy} onClick={onHide}>ยกเลิก</Button><Button form="edit-my-review-form" type="submit" disabled={busy || !!recovery}>บันทึกรีวิว</Button></Modal.Footer>
  </Modal>;
}
